// @flow

import React from 'react';
import 'intersection-observer';
import VisibilityObserver from 'react-intersection-observer';
import { minute } from 'metrick/duration';
import { getDateString, getRelativeDateString, type DateLike } from 'app/lib/date';

type Props = {|
  value: DateLike,
  updateFrequency: number,
  capitalized: boolean,
  seconds: boolean,
  fractionalSeconds: boolean,
  tabularNumerals: boolean,
  className?: string
|};

type State = {|
  text: string
|};

/**
 * Renders a friendly, human readable, self-updating representation of a timestamp,
 * as supplied via `value` and stored in `text`.
 */
export default class FriendlyTime extends React.PureComponent<Props, State> {
  static defaultProps = {
    updateFrequency: minute.bind(1),
    capitalized: true,
    seconds: false,
    fractionalSeconds: false,
    tabularNumerals: false
  };

  _timeout: TimeoutID;

  state = {
    text: ''
  };

  tick() {
    const { value, capitalized, seconds, fractionalSeconds } = this.props;

    this.setState(
      {
        text: getRelativeDateString(value, { capitalized, seconds, fractionalSeconds })
      },
      this.maybeScheduleTick
    );
  }

  componentDidMount() {
    this.maybeScheduleTick();
  }

  maybeScheduleTick() {
    const { updateFrequency } = this.props;

    if (typeof updateFrequency == 'number' && updateFrequency > 0) {
      this._timeout = setTimeout(() => {
        this.tick();
      }, updateFrequency);
    }
  }

  maybeCancelTick() {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }
  }

  componentWillUnmount() {
    this.maybeCancelTick();
  }

  componentDidUpdate({ updateFrequency }: Props) {
    if (updateFrequency !== this.props.updateFrequency) {
      this.maybeCancelTick();
      this.maybeScheduleTick();
    }
  }

  handleVisibilityChange = (visible: boolean) => {
    this.maybeCancelTick();

    if (visible) {
      this.tick();
    }
  }

  render() {
    return (
      <VisibilityObserver
        tag="time"
        className={this.props.className}
        dateTime={this.props.value}
        title={getDateString(this.props.value, true)}
        onChange={this.handleVisibilityChange}
      >
        {this.props.tabularNumerals ? (
          <span className="tabular-numerals">{this.state.text}</span>
        ) : this.state.text}
      </VisibilityObserver>
    );
  }
}
